import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';

interface CreateResolutionModalProps {
  companyId: string;
  onCreated: () => void;
}

export function CreateResolutionModal({ companyId, onCreated }: CreateResolutionModalProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [role, setRole] = useState<'ANY' | 'DIRECTOR' | 'CHAIR'>('DIRECTOR');
  const [quorum, setQuorum] = useState(50);
  const [loading, setLoading] = useState(false);

  async function create() {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required');
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase.from('board_resolutions').insert({
        company_id: companyId,
        title: title.trim(),
        body_md: body.trim(),
        required_role: role,
        quorum_pct: quorum / 100,
      });

      if (error) throw error;

      toast.success('Resolution created');
      setOpen(false);
      setTitle('');
      setBody('');
      setRole('DIRECTOR');
      setQuorum(50);
      onCreated();
    } catch (err: any) {
      console.error('Error creating resolution:', err);
      toast.error(err.message || 'Failed to create resolution');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="flex items-center gap-2">
          <PlusCircle className="h-4 w-4" /> New Resolution
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Board Resolution</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="Enter resolution title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="body">Body (Markdown)</Label>
            <Textarea
              id="body"
              placeholder="Enter resolution body in markdown format"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="role">Required Role</Label>
              <Select value={role} onValueChange={(value: any) => setRole(value)}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ANY">Any Member</SelectItem>
                  <SelectItem value="DIRECTOR">Directors</SelectItem>
                  <SelectItem value="CHAIR">Chair Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="quorum">Quorum (%)</Label>
              <Input
                id="quorum"
                type="number"
                min={0}
                max={100}
                value={quorum}
                onChange={(e) => setQuorum(parseInt(e.target.value || '0'))}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={create} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Resolution'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
